import { useCallback, useState } from "react";
import { t } from "../localization";
import { ItemSearchState, SortBy, SortDir, SortOption } from "../types/search";

export const getSortOptions = () => {
  const sortOptions: SortOption[] = [
    {
      label: t('item-search.name_ascending'),
      value: {
        sortDir: 'asc',
        sortBy: 'name'
      }
    },
    {
      label: t('item-search.name_descending'),
      value: {
        sortDir: 'desc',
        sortBy: 'name'
      }
    },
    {
      label: t('item-search.date_ascending'),
      value: {
        sortDir: 'asc',
        sortBy: 'date'
      }
    },
    {
      label: t('item-search.date_descending'),
      value: {
        sortDir: 'desc',
        sortBy: 'date'
      }
    }
  ];

  return sortOptions;
};

export const useItemsSearch = () => {
  const [state, setState] = useState<ItemSearchState>({
    sortDir: "asc",
    sortBy: "name",
    query: ""
  });

  const handleSearchChange = useCallback((query: string): void => {
    setState((prevState) => ({...prevState, query}));
  }, []);

  const handleSortChange = useCallback((sortDir: SortDir, sortBy: SortBy): void => {
    setState((prevState) => ({...prevState, sortDir, sortBy}));
  }, []);

  const sortByDate = (items: any[], sortDir: SortDir) => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.timeCreated);
      const dateB = new Date(b.timeCreated);

      return (sortDir === "asc")
        ? (dateA > dateB ? 1 : -1)
        : (dateB > dateA ? 1 : -1);
    });
  };

  const sortByName = (items: any[], sortDir: SortDir) => {
    return [...items].sort((a, b) => {
      const nameA = a.name as string;
      const nameB = b.name as string;

      return (sortDir === "asc")
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  };

  const handleSearch = (items: any[]) => {
    const query = state.query;
    if (!query) return items;

    const queryParts = query
      .split(" ")
      .map(x => x.toLowerCase());

    return items.filter(project => {
      const nameParts = project.name
        .split(" ")
        .map(x => x.toLowerCase());

      return queryParts.filter(text => {
        return nameParts.filter(x => x.includes(text)).length > 0;
      }).length === queryParts.length;
    });
  };

  const handleSort = (items: any[]) => {
    const {sortDir, sortBy} = state;
    if (!sortDir) return items;

    // Initial sorting by date is always applied
    let initialSort = sortByDate(items, "asc");

    if (sortBy === "date") {
      return sortByDate(initialSort, sortDir);
    }

    return sortByName(initialSort, sortDir);
  };

  return {
    handleSearch,
    handleSearchChange,
    handleSort,
    handleSortChange,
    state
  };
};